import { Icon } from '@iconify/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isMacOs } from 'react-device-detect';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import { ParagraphList } from '@/Domain/components/KnowledgeBase/ParagraphsSheet/ParagraphList/ParagraphList';
import { SavingBadge } from '@/Domain/components/KnowledgeBase/ParagraphsSheet/SavingBadge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Kbd } from '@/Domain/components/Shared/Kbd/Kbd';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { ArticleTypeEnum } from '@/Domain/enum/ArticleType.enum';
import { useCreateParagraph } from '@/Domain/hooks/KnowledgeBase/useCreateParagraph/useCreateParagraph.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export const ParagraphsSheet = () => {
  const { t } = useTranslation('paragraphs');
  const [query, setQuery] = useDebounceValue('', 300);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const listContainerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { open, isOpen, closeModal } = useModalStore();

  const { handleCreateParagraph } = useCreateParagraph();

  const handleAddParagraph = useCallback(
    () =>
      handleCreateParagraph({ type: ArticleTypeEnum.Paragraph, content: '' }).then(() =>
        listContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' }),
      ),
    [handleCreateParagraph],
  );

  const isSheetOpen = isOpen(MODAL_NAMES.PARAGRAPH_SHEET);

  useHotkeys(
    ['meta+n', 'ctrl+n'],
    (event) => {
      event.preventDefault();
      void handleAddParagraph();
    },
    [handleAddParagraph, isSheetOpen],
    { enabled: isSheetOpen },
  );

  useEffect(
    () => () => {
      if (!isOpen(MODAL_NAMES.PARAGRAPH_SHEET)) {
        setQuery('');
      }
    },
    [open],
  );

  useHotkeys(
    ['ctrl+k', 'meta+k'],
    () => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    },
    [],
  );

  useHotkeys(
    ['esc'],
    () => {
      if (searchRef.current && document.activeElement === searchRef.current) {
        setQuery('');
        searchRef.current.value = '';
      }
    },
    [],
    { enableOnFormTags: true, enableOnContentEditable: true },
  );

  useHotkeys(['esc'], () => closeModal(), []);

  const handleNeedHelpClick = () =>
    window.open('https://docs.quickchat.ai/ai-agent/knowledge-base', '_blank');

  return (
    <Sheet open={isSheetOpen} onOpenChange={(open) => !open && closeModal()}>
      <SheetContent
        className="!max-w-[100vh] w-3/4 overflow-hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <SheetHeader className="px-1">
          <section className="flex flex-col gap-1.5">
            <SheetTitle>{t('modal.title')}</SheetTitle>
            <SheetDescription>{t('modal.description')}</SheetDescription>
          </section>
          <section className="flex items-center gap-3">
            <SavingBadge />
            <Button variant="secondary" onClick={() => closeModal()}>
              {t('button.close')}
              <Kbd keyLabel="Esc" />
            </Button>
            <Button
              data-testid="add-paragraph"
              variant="dark"
              onClick={() => void handleAddParagraph()}
            >
              <Icon icon="mingcute:paragraph-line" />
              {t('button.add_snipped')}
              {isMacOs ? (
                <div className="flex items-center gap-1">
                  <Kbd keyLabel="⌘" variant="dark" />
                  <Kbd keyLabel="N" variant="dark" />
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <Kbd keyLabel="Ctrl" variant="dark" />
                  <Kbd keyLabel="N" variant="dark" />
                </div>
              )}
            </Button>
          </section>
        </SheetHeader>

        <div className="flex flex-col gap-3 flex-1 p-1 overflow-hidden">
          <Input
            ref={searchRef}
            defaultValue=""
            data-testid="paragraph-search"
            placeholder={t('modal.list.search')}
            onChange={(event) => setQuery(event.target.value)}
            startIcon={<Icon icon="mingcute:search-line" className="text-muted-foreground" />}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            endIcon={
              isSearchFocused ? (
                <Kbd keyLabel="Esc" size="md" />
              ) : (
                <div className="flex gap-1 items-center">
                  {isMacOs ? (
                    <>
                      <Kbd keyLabel={<span className="text-sm">{'⌘'}</span>} />
                      <Kbd keyLabel="K" size="md" />
                    </>
                  ) : (
                    <>
                      <Kbd keyLabel="Ctrl" />
                      <Kbd keyLabel="K" size="md" />
                    </>
                  )}
                </div>
              )
            }
          />
          <Separator />
          <ParagraphList ref={listContainerRef} query={query} />
        </div>

        <SheetFooter className="shrink-0 px-1">
          <Button variant="link" className="!px-0" onClick={handleNeedHelpClick}>
            <Icon
              icon="mingcute:question-line"
              className="mr-2 h-5 w-5 text-foreground-subtle transition-colors group-hover:text-muted-foreground"
            />
            <span className="text-sm font-normal !text-muted-foreground">
              {t('button.need_help')}
            </span>
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
